import { ecsClientSettings } from '../initializeAnalytics';
import { AriaDatapoint } from '../datapoints/AriaDatapoint';
import { CustomDataMap } from 'owa-analytics-types';
import { UserActionEvent, RequiredProperties } from '../types/EcsTelemetrySettingTypes';

// For lgacy events being logged as unified, this manually maps required properties
export function addLegacyEventUnifiedProperties(
    datapoint: AriaDatapoint,
    userActionEvent: UserActionEvent
): void {
    if (userActionEvent.requiredProperties.length > 0) {
        const firstProp = userActionEvent.requiredProperties[0];
        if (isValidJsonRequiredProperty(firstProp)) {
            const jsonField = JSON.parse(<string>datapoint.properties[firstProp.parentJsonField]);
            switch (userActionEvent.actionName) {
                case 'SendMail': {
                    const legacyValue = <number>jsonField['operation'];
                    if (!isNaN(legacyValue)) {
                        delete jsonField.operation;
                        jsonField[firstProp.name] =
                            ecsClientSettings.sharedEnums.mailComposeOperation[legacyValue];
                    }
                    break;
                }
                case 'ComposeMail': {
                    const composeOperationsMap = {
                        MailComposeNew: 0,
                        MailComposeReply: 1,
                        MailComposeReplyAll: 2,
                        MailComposeForward: 3,
                        MailComposeLoadDraft: 4,
                    };
                    const value = isNaN(composeOperationsMap[datapoint.eventName])
                        ? 7
                        : composeOperationsMap[datapoint.eventName];
                    jsonField[firstProp.name] =
                        ecsClientSettings.sharedEnums.mailComposeOperation[value];
                    break;
                }
                case 'DeleteMail': {
                    const mailTypeMap = {
                        TnS_DeleteItem: 0,
                        TnS_DeleteConversation: 1,
                        TnS_DeleteSelectedConversations: 1,
                    };
                    const value = isNaN(mailTypeMap[datapoint.eventName])
                        ? 2
                        : mailTypeMap[datapoint.eventName];
                    jsonField[firstProp.name] = ecsClientSettings.sharedEnums.mailType[value];
                    break;
                }
                default:
                    break;
            }
            datapoint.properties[firstProp.parentJsonField] = <string>JSON.stringify(jsonField);
        }
    }
}

// Rebuilds the datapoint's properties collection to the unified schema
export function buildUnifiedDataMap(datapoint: AriaDatapoint, userActionEvent: UserActionEvent) {
    return <CustomDataMap>{
        ActionName: userActionEvent.actionName,
        ActionSource: datapoint.properties?.ActionSource,
        ActionData: <string>datapoint.properties.MiscData,
        CosmosOnlyData: <string>datapoint.properties.ExtraData,
        E2ETimeElapsed: <number>datapoint.properties.E2ETimeElapsed,
        SampleRate: <number>datapoint.properties.Sampled,
        SessionElapseTime: <number>datapoint.properties.SessionElapseTime,
        SessionOccurence: <number>datapoint.sessionOccurence,
        SequenceNumber: <number>datapoint.properties.SequenceNumber,
        Status: <string>datapoint.properties.Status,
        DiagnosticPrivacyLevel:
            ecsClientSettings.sharedEnums.diagnosticPrivacyLevel[<number>userActionEvent.dpl],
        PrivacyDataTypes: buildPrivacyDataTypes(userActionEvent),
    };
}

// Array of PrivacyDataTypes applicable to the datapoint.
// It needs to be logged as a string
function buildPrivacyDataTypes(userActionEvent: UserActionEvent): string {
    return userActionEvent.pdt
        .map(e => ecsClientSettings.sharedEnums.privacyDataType[e])
        .toString();
}

function isValidJsonRequiredProperty(requiredProperty: RequiredProperties): boolean {
    if (requiredProperty?.parentJsonField && requiredProperty.name && requiredProperty.valueType) {
        return requiredProperty.valueType !== 'enum' || requiredProperty?.enumType !== undefined;
    }
    return false;
}
